import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  useGetProviderBasicProfile,
  useGetProviderCompanyProfile,
  useGetProviderAttachments,
  useListOfProviderLocations,
} from "../../custom-hooks/queries";
import { useVendorProfileLibraryServices } from "../../custom-hooks/useVendorProfileLibraryServices";
import ProviderOverviewSkeleton from "../ProviderOverviewSkeletons";
import Notification from "../Notification";
import Capabilities from "../Capabilities";
import { getLocationFromProvider } from "../../utils/addressUtils";
import { resolveVendorProfileServiceCategories } from "../../utils/vendorProfileCapabilities";

const socialIcons = {
  LinkedIn: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
    </svg>
  ),
  Facebook: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
    </svg>
  ),
  Twitter: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
    </svg>
  ),
  X: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
    </svg>
  ),
  Instagram: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.40z"></path>
    </svg>
  ),
  YouTube: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"></path>
    </svg>
  ),
};

const Overview = ({ services: servicesFromParent, documents } = {}) => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  const { id } = useParams();
  const { data: basicProfile, isLoading } = useGetProviderBasicProfile(id);
  const { data: companyProfile } = useGetProviderCompanyProfile(id);
  const { data: locations } = useListOfProviderLocations(id);
  const { libraryDocuments } = useVendorProfileLibraryServices(id);
  const { rfis } = useSelector((state) => state.rfisColors);
  const serviceCategories = useMemo(
    () =>
      resolveVendorProfileServiceCategories({
        profile: basicProfile || servicesFromParent,
        documents: documents ?? libraryDocuments,
        rfisList: rfis,
      }),
    [basicProfile, servicesFromParent, documents, libraryDocuments, rfis]
  );

  // Social links from API: only render if both type and url exist
  const socialLinksArr = Array.isArray(companyProfile?.social_link)
    ? companyProfile.social_link
    : [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {isLoading ? (
        <ProviderOverviewSkeleton />
      ) : (
        <>
          <div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {/* Website */}
              <div className="pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">Website</dt>
                <dd className="mt-1 text-sm text-blue-600 hover:text-blue-800">
                  <a
                    href={
                      basicProfile?.url
                        ? basicProfile.url.startsWith("http")
                          ? basicProfile.url
                          : `https://${basicProfile.url}`
                        : "#"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {basicProfile?.url || "Visit Site"}
                  </a>
                </dd>
              </div>
              {/* Socials */}
              <div className="pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">Socials</dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {socialLinksArr.filter(
                    (link) => link.type && link.url && socialIcons[link.type]
                  ).length === 0 ? (
                    <span className="text-gray-700">-</span>
                  ) : (
                    <div className="flex items-center space-x-4">
                      {socialLinksArr
                        .filter(
                          (link) =>
                            link.type && link.url && socialIcons[link.type]
                        )
                        .map((link, i) => (
                          <a
                            key={link.type + i}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-slate-600"
                            aria-label={link.type}
                          >
                            {socialIcons[link.type]}
                            <span className="sr-only">{link.type}</span>
                          </a>
                        ))}
                    </div>
                  )}
                </dd>
              </div>
              {/* Est. Date */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">Est. Date</dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {basicProfile?.year_founded || "-"}
                </dd>
              </div>
              {/* Employees */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">Employees</dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {companyProfile?.number_of_employees_range || "-"}
                </dd>
              </div>
              {/* Primary Location */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">
                  Primary Location
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {getLocationFromProvider(
                    locations?.[0]?.provider_locations?.find(loc => loc?.location_type === "primary"),
                    { short: false, abbvState: true }
                  ) || "-"}
                </dd>
              </div>
              {/* Secondary Locations */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-900 ">
                    Additional Locations
                </dt>
                  {locations?.[0]?.provider_locations?.length > 0 ?
                    (locations?.[0]?.provider_locations?.filter(loc => loc?.location_type !== "primary")
                      ?.sort((a, b) => a.created_at > b.created_at ? 1 : -1)?.map((loc) => (
                      <dd className="mt-1 text-sm text-gray-700">
                        <span key={loc?.provider_location_id}>
                          {getLocationFromProvider(loc, { short: false, abbvState: true })}
                        </span>
                      </dd>
                    ))) : <span className="text-gray-400">–</span>}
              </div>
              {/* About Us */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-2">
                <dt className="text-sm font-medium text-gray-900">About Us</dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {basicProfile?.description || "-"}
                </dd>
              </div>
              {/* Service Categories */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-2">
                <dt className="text-sm font-medium text-gray-900">
                  Service Categories
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {serviceCategories.length > 0 ? (
                    <Capabilities
                      services={serviceCategories.map(({ capability }) => capability)}
                      applyRfiCatalogCustomLabels={true}
                    />
                  ) : (
                    <span className="text-gray-400">–</span>
                  )}
                </dd>
              </div>
              {/* Therapeutic Areas & Indications */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-2">
                <dt className="text-sm font-medium text-gray-900">
                  Therapeutic Areas & Indications
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  <div className="space-y-3">
                    {basicProfile?.therapeutic_areas_supported?.length > 0 ? (
                      basicProfile.therapeutic_areas_supported.map((ta, i) => {
                        const indications =
                          basicProfile?.indications
                            ?.filter((ind) => ind.therapeutic_area_id === ta.id)
                            .map((ind) => ind.indication)
                            .join(", ") || "-";
                        return (
                          <div className="flex items-start" key={i}>
                            <div className="w-48 flex-shrink-0">
                              <span className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-300">
                                {ta.therapeutic_area}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 leading-relaxed flex-1">
                              {indications}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <span className="text-gray-400">–</span>
                    )}
                  </div>
                </dd>
              </div>
              {/* Other Capabilities */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-2">
                <dt className="text-sm font-medium text-gray-900">
                  Other Capabilities
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {basicProfile?.other_capabilities?.length > 0 ? (
                    basicProfile.other_capabilities.map((cap, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-600/10 mr-2 mb-1"
                      >
                        {cap}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">–</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
          {/* Additional Details */}
          <div className="mt-8 px-4 sm:px-0">
            <h3 className="text-base font-semibold leading-7 text-gray-900">
              Additional Details
            </h3>
          </div>
          <div className="mt-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {/* Languages */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">Languages</dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {basicProfile?.languages?.join(", ") || "-"}
                </dd>
              </div>
              {/* Recent Annual Revenue */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">
                  Recent Annual Revenue
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {companyProfile?.annual_revenue_range ||
                    companyProfile?.annual_revenue_range ||
                    "-"}
                </dd>
              </div>
              {/* Diversity Classifications */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">
                  Diversity Classifications
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {companyProfile?.diversity_classification?.length > 0 ? (
                    companyProfile.diversity_classification
                      .map((item) => item.diversity_classification)
                      .join(", ")
                  ) : (
                    <span className="text-gray-400">–</span>
                  )}
                </dd>
              </div>
              {/* Affiliated Organizations */}
              <div className="border-t border-gray-100 pt-4 sm:col-span-1">
                <dt className="text-sm font-medium text-gray-900">
                  Affiliated Organizations
                </dt>
                <dd className="mt-1 text-sm text-gray-700">
                  {companyProfile?.affiliated_organizations?.length > 0 ? (
                    companyProfile.affiliated_organizations.map((org, idx) => (
                      <span key={idx}>
                        <span className="text-gray-700">{org.name || org}</span>
                        {idx <
                        companyProfile.affiliated_organizations.length - 1
                          ? ", "
                          : ""}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">–</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
          {/* Notification (unchanged) */}
          <Notification
            show={false}
            setShow={() => {}}
            errorMessage={""}
            status={"error"}
          />
        </>
      )}
    </div>
  );
};

export default Overview;
